import type * as React from "react";
import { useLayoutEffect, useState } from "react";
import { useTranslation, type WithTranslation } from "react-i18next";
import { DownloadUrlLink } from "../../../common/components/Link";
import Pagination from "../../../Search/components/common/Pagination";
import { type PublicationDownloadFile } from "../../../models/ProductData";
import { CURRENT_LANGUAGE, PUBS_ON_DESKTOP, PUBS_ON_MOBILE } from "../../../utils/constants";
import { SimpleSelect } from "../../../common/components/Select/SimpleSelect";
import useMediaQuery from "../../../common/hooks/useMediaQuery";
import PublicationPopup from "../PublicationPopup";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { trackPublicationDownload } from "../../../feature/Analytics/ga/ga";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "../../../utils/screenSizes";
import { type DropdownEntry } from "~/common/types";
import { Alert } from "~/common/m3/Alert/Alert";

interface PublicationsDownloadTabProps {
  publications: PublicationDownloadFile[];
}

const PublicationsDownloadTabOuter = (props: PublicationsDownloadTabProps) => {
  // just additional check
  if (props.publications.length === 0) {
    return <NoPublicationsComponent />;
  }

  return <PublicationsDownloadTab {...props} />;
};

const PublicationsDownloadTab: React.FC<PublicationsDownloadTabProps> = (props) => {
  const { t } = useTranslation();
  const publicationsBulk = props.publications;

  // filtered publications
  const [filteredPublications, setFilteredPublications] = useState<PublicationDownloadFile[]>(publicationsBulk);

  // responsivenesss...
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const PUBLICATIONS_ON_PAGE = isDesktop ? PUBS_ON_DESKTOP : PUBS_ON_MOBILE;
  // setting current page index
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(() =>
    getPublicationsDownloadsNumberPages(filteredPublications, PUBLICATIONS_ON_PAGE),
  );

  // render ready publications
  const [renderedPublications, setRenderedPublications] = useState<PublicationDownloadFile[]>(() =>
    getPublicationsDownloadsDocumentsPerPage(filteredPublications, PUBLICATIONS_ON_PAGE, currentPage),
  );
  const renderPublications = () => {
    setRenderedPublications(
      getPublicationsDownloadsDocumentsPerPage(filteredPublications, PUBLICATIONS_ON_PAGE, currentPage),
    );
  };

  // get available categories from publications bulk
  const categories = ["all", ...getPublicationsDownloadsCategories(publicationsBulk)];
  const [currentCategory, setCurrentCategory] = useState<string>(categories[0]);

  // get available languages from publications bulk
  const languages = ["all", ...getPublicationsDownloadsLanguages(publicationsBulk)];
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => selectInitialLanguage(languages, t));

  // detect fields change
  useLayoutEffect(() => {
    // update the filtered publications
    getFilteredPublicationsDownloadFiles(
      publicationsBulk,
      currentCategory !== categories[0] ? currentCategory : undefined,
      currentLanguage !== languages[0] ? currentLanguage : undefined,
    ).then(setFilteredPublications);
  }, [currentCategory, currentLanguage]);

  // filtered publications have been updated
  useLayoutEffect(() => {
    // reset page navigation
    setCurrentPage(0);
    // update pagination
    setTotalPages(getPublicationsDownloadsNumberPages(filteredPublications, PUBLICATIONS_ON_PAGE));
    // update render list pro specific page
    renderPublications();
  }, [filteredPublications]);

  // check page changed
  useLayoutEffect(() => {
    // update render list pro specific page
    renderPublications();
  }, [currentPage]);

  // publication popup
  const { val: statePopup, setTrue: showPopup, setFalse: hidePopup } = useBooleanState(false);
  const [publicationsPopup, setPublicationsPopup] = useState<PublicationDownloadFile[]>([]);
  // set publication for popup and show it
  const showPublicationPopup = async (publicationID: string) => {
    if (!publicationID || publicationID.trim() === "") {
      return;
    }

    let publicationsPopupList: PublicationDownloadFile[] = [];
    try {
      publicationsPopupList = await new Promise((resolve) => {
        window.setTimeout(() => {
          const data = publicationsBulk.filter((publication) => publication.pubid === publicationID);
          resolve(data);
        }, 0);
      });
    } catch (error) {
      // this should never be reached
      console.error("modal - filtering publications error", error);
    }

    // prevent showing popup if there is nothing in the list after filtering
    if (publicationsPopupList.length === 0) {
      return;
    }
    // set publications found with the same pubid
    setPublicationsPopup(publicationsPopupList);
    // make popup visible
    showPopup();
  };

  // disable field selection if only one value is available
  const disableCategory = categories.length === 1;
  const disableLanguage = languages.length === 1;

  return (
    <div className={"scha__publications-downloads-tab" + (!isDesktop ? " mobile" : "")} id="downloads-content">
      <div className="scha__publications-downloads-tab__filter-box row">
        <div className="scha__publications-downloads-tab__filter-box__category col-sm-4">
          <SimpleSelect
            label={t("productDetailsPage.tabs.downloads.filterBox.category")}
            values={makePublicationsDropdownEntriesCategories(categories, t)}
            fullWidth={true}
            onChange={(e) => setCurrentCategory(e.target.value as string)}
            defaultValue={categories[0]}
            disabled={disableCategory}
          />
        </div>
        <div className="scha__publications-downloads-tab__filter-box__language col-sm-4">
          <SimpleSelect
            label={t("productDetailsPage.tabs.downloads.filterBox.language")}
            values={makePublicationsDropdownEntriesLanguages(languages, t)}
            fullWidth={true}
            onChange={(e) => setCurrentLanguage(e.target.value as string)}
            value={currentLanguage}
            disabled={disableLanguage}
          />
        </div>
      </div>

      {renderedPublications.length === 0 && (
        <Alert severity={"info"} className={"scha__publications-downloads-tab__no-results"}>
          {t("productDetailsPage.tabs.downloads.filterBox.noResults")}
        </Alert>
      )}

      {renderedPublications.length > 0 && (
        <>
          {statePopup && publicationsPopup && publicationsPopup.length > 0 && (
            <PublicationPopup
              close={hidePopup}
              publications={publicationsPopup}
              defaultLanguage={currentLanguage !== languages[0] ? currentLanguage : undefined}
            />
          )}
          <div className="scha__publications-downloads-tab__publications-list">
            {renderedPublications.map((publication, i) => (
              <PublicationTile
                publication={publication}
                key={i}
                onClick={() => showPublicationPopup(publication.pubid)}
              />
            ))}
          </div>
          <div className="d-flex flex-justify-center">
            {totalPages > 1 && (
              <Pagination
                pageAmount={totalPages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                goToIdAfterChange={"#downloads-content"}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

const NoPublicationsComponent: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Alert severity={"info"} className={"scha__publications-downloads-tab__no-results"}>
      {t("productDetailsPage.tabs.downloads.filterBox.noPublications")}
    </Alert>
  );
};

interface PublicationTileProps {
  publication: PublicationDownloadFile;
  onClick?: () => void;
}
const PublicationTile: React.FC<PublicationTileProps> = ({ publication, onClick }) => {
  const { t } = useTranslation();
  const metaText =
    publication.year +
    " | " +
    publication.mediaCategory +
    " | " +
    publication.shortTitle +
    " | " +
    publication.language;

  return (
    <div className="scha__publication-tile">
      <div className="scha__publication-tile__wrapper" onClick={onClick}>
        <div className="scha__publication-tile__cover">
          {publication.image.trim() !== "" && <img src={publication.image} alt={publication.shortTitle} />}
        </div>
        <div className="scha__publication-tile__content">
          <div className="scha__publication-tile__content__meta-data" title={metaText}>
            {metaText}
          </div>
          <div className="scha__publication-tile__content__title">
            <h6 title={publication.titleRaw}>{publication.titleRaw}</h6>
          </div>
        </div>
      </div>
      <div className="scha__publication-tile__wrapper__download">
        <DownloadUrlLink
          href={publication.href}
          target={"_blank"}
          onClick={() => trackPublicationDownload(publication)}
        >
          {t("productDetailsPage.tabs.downloads.tiles.button.download")}
        </DownloadUrlLink>
      </div>
    </div>
  );
};

export default PublicationsDownloadTabOuter;

export const getPublicationsDownloadsCategories = (publications: PublicationDownloadFile[]) => {
  // check params publications for invalid data,
  if (publications.length === 0) {
    return [];
  }

  const categories: string[] = [];

  publications.forEach(
    (publication) =>
      categories.indexOf(publication.mediaCategoryCode) < 0 && categories.push(publication.mediaCategoryCode),
  );

  return categories;
};

export const getPublicationsDownloadsLanguages = (publications: PublicationDownloadFile[]) => {
  // check params publications for invalid data,
  if (publications.length === 0) {
    return [];
  }

  const languages: string[] = [];

  publications.forEach(
    (publication) => languages.indexOf(publication.languageCode) < 0 && languages.push(publication.languageCode),
  );

  return languages;
};

export const getPublicationsDownloadsNumberPages = (publications: PublicationDownloadFile[], limit: number) => {
  // check params publications for invalid data,
  if (publications.length === 0 || limit <= 0 || limit >= publications.length) {
    return 1;
  }
  return Math.ceil(publications.length / limit);
};

export const getPublicationsDownloadsDocumentsPerPage = (
  publications: PublicationDownloadFile[],
  limit: number,
  page: number,
) => {
  // check params publications for invalid data,
  if (publications.length === 0 || limit <= 0 || page < 0) {
    return [];
  }

  // check if page is available, if not, give the results for the last available page
  let lastPage = getPublicationsDownloadsNumberPages(publications, limit);
  if (page < lastPage) {
    lastPage = page;
  }

  // explicit indexes
  const start = lastPage * limit;
  const end = start + limit;

  // return part from the publications based on page
  const result = publications.slice(start, end);

  return result;
};

export const getFilteredPublicationsDownloadFiles = async (
  publications: PublicationDownloadFile[],
  category?: string,
  language?: string,
) => {
  try {
    let bulk = [...publications];
    // filter for categories
    if (category) {
      // prevent browser render lock for filtering big lists
      bulk = await new Promise((resolve) => {
        window.setTimeout(() => {
          const data = bulk.filter((publication) => publication.mediaCategoryCode === category);
          resolve(data);
        }, 0);
      });
    }
    // filter for languages
    if (language) {
      // prevent browser render lock for filtering big lists
      bulk = await new Promise((resolve) => {
        window.setTimeout(() => {
          const data = bulk.filter((publication) => publication.languageCode === language);
          resolve(data);
        }, 0);
      });
    }
    return bulk;
  } catch (error) {
    // this should never be reached
    console.error("filtering publications error", error);
  }
  return [];
};

export const makePublicationsDropdownEntriesCategories = (list: string[], t: WithTranslation["t"]) =>
  makeDropdownEntries(list, t, "productDetailsPage.tabs.downloads.filterBox.categories.option.");

export const makePublicationsDropdownEntriesLanguages = (list: string[], t: WithTranslation["t"]) =>
  makeDropdownEntries(list, t, "productDetailsPage.tabs.downloads.filterBox.languages.option.");

const makeDropdownEntries = (list: string[], t: WithTranslation["t"], translationPrefix: string) => {
  if (list.length === 0) {
    return [];
  }
  const optionsList: { id: string; label: string }[] = list.map((option) => ({
    id: option,
    label: t(translationPrefix + option),
  }));
  optionsList.sort((a, b) => a.label.localeCompare(b.label));
  return optionsList.map(
    (option) =>
      ({
        label: option.label,
        value: option.id,
      }) as DropdownEntry<string>,
  );
};

const selectInitialLanguage = (list: string[], t: WithTranslation["t"]) => {
  const currLanguage = CURRENT_LANGUAGE.toUpperCase();
  const selectedCode =
    list.find((code) => t(`data.publicationLanguage.${code}.code`, { lng: "en" }) === currLanguage) ?? "all";
  return selectedCode;
};
